import { Fragment, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";

import { useErrorModal } from "@/components/errorModal";
import { Loader } from "@/components/loader";
import { apiResolver, useApi } from "@/api/useApi";

import { useSimulator } from "./context/SimulatorWrapper";
import { ChatReleaseGurad } from "./parts/chat";
import { TopicsList } from "./parts/topicsList/TopicsList";
import { NewLeftPanel } from "./parts/topicsList/NewLeftPanel";
import { RightPanel } from "./parts/rightPanel";
import styles from "./styles.module.scss";
import { useChat } from "./context/ChatWrapper";
import { TicketTranscriptResponse } from "../interaction-view/TicketView";
import { toast } from "sonner";
import {
  SnippetCreationManager,
  SnippetCreationManagerRef,
} from "./parts/snippet-creation/SnippetCreationManager";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";

export const Simulator = ({
  hideNavbar,
  selectedTopicId,
  selectedTicketId,
  onCloseSimulator,
  hideTopicsList,
}: {
  hideNavbar?: boolean;
  selectedTopicId?: string;
  selectedTicketId?: string;
  onCloseSimulator?: (isDone?: boolean) => void;
  hideTopicsList?: boolean;
}) => {
  const navigate = useNavigate();
  const { api } = useApi();

  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const snippetCreationManagerRef = useRef<SnippetCreationManagerRef>(null);

  const { simulator, isLoading, topics, isSimulatorLoading, isLoadingTopics } =
    useSimulator();
  const { handleAddMessage, isProcessingMessage } = useChat();
  const { openToggle, ErrorModal } = useErrorModal();
  const newLeftPanelEnabled = useFlag("newSimulatorLeftPanel", false);

  useEffect(() => {
    if (simulator?.channel === "knowledge_hub") {
      navigate(`/automation/${simulator.id}/configurations/knowledge-hub`);
    } else if (simulator?.channel === "copilot") {
      navigate(`/automation/${simulator.id}/configurations/copilot`);
    }
  }, [simulator]);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasProcessedTranscript = useRef(false);

  useEffect(() => {
    if (!isLoading && !simulator) {
      timeoutRef.current = setTimeout(() => {
        openToggle();
      }, 1000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [simulator, isLoading, openToggle]);

  // for tracker - kill this
  const transcriptQuery = useQuery({
    queryKey: ["simulator", "transcript", selectedTicketId],
    queryFn: () =>
      apiResolver<TicketTranscriptResponse>(
        api.get(
          `/platform/tickets/${selectedTicketId}/comments?includeContextExport=true`,
        ),
      ),
    enabled: !!selectedTicketId,
  });

  // for tracker - kill this
  useEffect(() => {
    if (
      transcriptQuery.data &&
      !hasProcessedTranscript.current &&
      !isProcessingMessage
    ) {
      const comments = transcriptQuery.data.comments.sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );

      handleAddMessage({
        messages: [
          {
            content: comments[0].body,
            role: "user",
          },
        ],
        ticketId: selectedTicketId,
      });

      hasProcessedTranscript.current = true;
    }
  }, [
    transcriptQuery.data,
    handleAddMessage,
    selectedTicketId,
    isProcessingMessage,
  ]);

  // Only show loader while simulator itself is loading
  if (
    isLoadingTopics ||
    !topics ||
    topics.length === 0 ||
    isSimulatorLoading ||
    !simulator
  ) {
    return <Loader />;
  }

  // Only redirect if topics have finished loading and there are none
  // Don't redirect while topics are still loading
  if (!isLoadingTopics && (!topics || topics.length === 0)) {
    toast.error(
      "No topics found, make sure there is at least one topic in the workspace.",
      { id: "no-topics-error-simulator" },
    );

    return <Navigate to={`/training-center/${simulator.id}`} />;
  }

  return (
    <Fragment>
      <div className={styles.container}>
        <div className={styles.mainContainer}>
          {!hideTopicsList &&
            (newLeftPanelEnabled ? (
              <NewLeftPanel isLoadingMessages={isLoadingMessages} />
            ) : (
              <TopicsList isLoadingMessages={isLoadingMessages} />
            ))}
          <div className={styles.chatContainer}>
            <ChatReleaseGurad
              onCloseSimulator={onCloseSimulator}
              setIsLoadingMessages={setIsLoadingMessages}
              onCreateSnippetClick={() =>
                snippetCreationManagerRef.current?.openCreateDialog()
              }
            />
          </div>
          <RightPanel hideNavbar={hideNavbar} />
        </div>
      </div>
      <ErrorModal
        title="Auto-Pilot not found"
        description="It might happen due to changing of workspaces"
        actionText="Go back"
        onAction={() => {
          navigate("/automation?f=autopilot");
        }}
        disableBackgroundClick
      />
      <SnippetCreationManager ref={snippetCreationManagerRef} />
    </Fragment>
  );
};
